"use client"
import { ArrowsIndexLeft, ArrowsIndexRight } from "@/public/icons/ArrowsIndex"
import { iconsMap } from "@/public/icons/iconsMap"
import styled from "@emotion/styled"
import { Box } from "@mui/material"
import { useTranslations } from "next-intl"
import Image from "next/image"
import { useEffect, useState } from "react"
import { useInView } from "react-intersection-observer"
import {
  colors,
  sizes,
  transitionDelayTime,
  useInViewConfig,
  useIsDesktop,
  useTransitionUpStyles,
} from "ui"
import { mediaQueries } from "ui/theme/mediaQueries"
import { extractMenuProperties, getUmbracoMediaUrl } from "utils"
import { sendBlockEvent } from "utils/gtm"
import CommonTitle from "../common/bit/CommonTitle"
import BlockWrapper from "../common/BlockWrapper"
import StripWithIconAndArrow from "./StripWithIconAndArrow"

const StyledImageContainer = styled(Box)`
  position: relative;
  bottom: 0;
  img {
    object-fit: cover;
  }
  ${mediaQueries.mobile} {
    height: 340px;
    width: calc(100% + 48px);
    margin-inline-start: -24px;
    margin-inline-end: -24px;
  }

  ${mediaQueries.tablet} {
    height: 340px;
    width: 100%;
    margin-inline-start: unset;
    margin-inline-end: unset;
    img {
      object-fit: contain;
    }
  }

  ${mediaQueries.desktopM} {
    height: 400px;
    width: 762px;
    margin-bottom: -120px;
  }

  ${mediaQueries.desktopXl} {
    height: 582px;
    width: 1125px;
  }
`

const StyledGradientLayer = styled(Box)`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    0deg,
    ${colors.lightGray} -0.14%,
    rgba(230, 229, 234, 0) 99.86%
  );
`

const StyledMobileStripWithIconAndArrow = styled(StripWithIconAndArrow)`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 8px;
  width: 100%;

  ${mediaQueries.mobile} {
    flex-direction: column;
    gap: 10px;
    padding: 0;
  }

  ${mediaQueries.tablet} {
    flex-direction: column;
    gap: 10px;
    padding: 0;
  }

  ${mediaQueries.desktopM} {
    flex-direction: row;
  }
`

const StyledServicesContainer = styled(Box)`
  display: flex;
  justify-content: center;
  flex-flow: row wrap;
  gap: ${sizes.baseHalf};
  width: 100%;
  ${mediaQueries.desktopM} {
    gap: ${sizes.baseSingle};
    position: absolute;
    justify-content: space-between;
    bottom: 40px;
    margin-bottom: 64px;
  }
  ${mediaQueries.desktopXl} {
    width: 100%;
    bottom: 130px;
  }
`

const StyledService = styled(Box)<{
  index: number
  isrightside: string
  isvisible: string
}>`
  display: flex;
  align-items: center;
  opacity: ${({ isvisible }) => (isvisible === "true" ? 1 : 0)};
  transform: ${({ isvisible }) =>
    isvisible === "true" ? "translateY(0)" : "translateY(20px)"};
  transition:
    opacity 0.8s ease-in-out,
    transform 0.8s ease-in-out;
  text-decoration: none;
  ${mediaQueries.mobile} {
    width: 160px;
    height: 141px;
    justify-content: center;
    opacity: ${({ isvisible }) => (isvisible === "true" ? 1 : 0)};
    transform: ${({ isvisible }) =>
      isvisible === "true" ? "translateY(0)" : "translateY(20px)"};
    transition:
      opacity 0.8s ease-in-out,
      transform 0.8s ease-in-out;
  }

  ${mediaQueries.tablet} {
    width: 186px;
    height: 152px;
    justify-content: center;
    opacity: ${({ isvisible }) => (isvisible === "true" ? 1 : 0)};
    transform: ${({ isvisible }) =>
      isvisible === "true" ? "translateY(0)" : "translateY(20px)"};
    transition:
      opacity 0.8s ease-in-out,
      transform 0.8s ease-in-out;
  }

  ${mediaQueries.desktopM} {
    position: absolute;
    width: 250px;
    margin-bottom: 0;
    ${({ index, isrightside }) => `
      bottom: ${index * 100}px;
      ${
        isrightside === "true"
          ? `right: ${index * 100 + 80}px;`
          : `left: ${index * 100 + 80}px;`
      }
    `}
  }

  ${mediaQueries.desktopXl} {
    width: 300px;
    margin-bottom: -120px;
    ${({ index, isrightside }) => `
      bottom: ${index * 130}px;
      ${
        isrightside === "true"
          ? `right: ${index * 100 + 80}px;`
          : `left: ${index * 100 + 80}px;`
      }
    `}
  }
`

const StyledBlockWrapper = styled(BlockWrapper)`
  align-items: center;
  margin-top: -120px;
  padding-top: 550px;
  ${mediaQueries.desktopM} {
    margin-top: -120px;
    padding-top: 160px;
  }
`

const Services = ({ id, data }: any) => {
  const [visibleIndex, setVisibleIndex] = useState(-1) // Start with no services visible
  const { ref, inView } = useInView(useInViewConfig)
  const transformedData = data
  const services = transformedData.servicesList.blocks
  const divransitionStyles = useTransitionUpStyles(inView, transitionDelayTime)
  const leftServices = services.slice(0, 3)
  const rightServices = services.slice(3, 6).reverse()
  const t = useTranslations("HomePage")

  const parseTitleToHour = () => {
    const currentHour = new Date().getHours()
    if (currentHour >= 5 && currentHour < 12) {
      return transformedData.titleMorning?.value
    } else if (currentHour >= 12 && currentHour < 17) {
      return transformedData.titleNoon?.value
    } else {
      return transformedData.titleNight?.value
    }
  }
  useEffect(() => {
    if (!inView) {
      return
    }
    // Show services one pair at a time
    if (isDesktop) {
      if (
        visibleIndex <
        Math.max(leftServices.length, rightServices.length) - 1
      ) {
        const timer = setTimeout(
          () => setVisibleIndex(visibleIndex + 1),
          transitionDelayTime * 1000,
        )
        return () => clearTimeout(timer) // Clear timeout on component unmount or re-render
      }
    } else {
      // Show services one after the other for mobile
      if (visibleIndex < services.length - 1) {
        const timer = setTimeout(
          () => setVisibleIndex(visibleIndex + 1),
          transitionDelayTime * 1000, // Adjust the delay time if needed
        )
        return () => clearTimeout(timer) // Clear timeout on component unmount or re-render
      }
    }
  }, [visibleIndex, inView])
  const isDesktop = useIsDesktop()

  return (
    <StyledBlockWrapper id={id}>
      {isDesktop && (
        <CommonTitle
          title={parseTitleToHour()}
          subtitle={transformedData?.subtitle?.value}
        />
      )}

      {transformedData?.image?.mediaItems && (
        <StyledImageContainer style={{ ...divransitionStyles }} ref={ref}>
          <Image
            alt={t("servicesImageAlt")}
            fill
            src={getUmbracoMediaUrl(transformedData?.image?.mediaItems[0]?.url)}
          />
          {!isDesktop && <StyledGradientLayer />}
        </StyledImageContainer>
      )}

      {isDesktop && (
        <StyledServicesContainer>
          {leftServices.map((service: any, index: number) => {
            const {
              url: link,
              logo,
              primaryColor,
            } = extractMenuProperties(service)
            const ArrowIcon = iconsMap[ArrowsIndexLeft[index]?.name]
            // GTM event handler for left services
            const handleClick = (e: React.MouseEvent) => {
              sendBlockEvent({
                blockId: id || "2",
                componentName: "שרות",
                title: parseTitleToHour(),
                productName: data?.formDefaultInsuranceType,
                actionName: undefined,
                buttonName: link?.name,
              })
            }
            return (
              <StyledService
                key={`left-${index}`}
                index={index}
                isrightside={"false"}
                isvisible={index <= visibleIndex ? "true" : "false"}
                onClick={handleClick}
              >
                <StripWithIconAndArrow
                  link={link}
                  ArrowIcon={ArrowIcon}
                  logo={logo}
                  bgcolor={primaryColor}
                />
              </StyledService>
            )
          })}

          {rightServices.map((service: any, index: number) => {
            const {
              url: link,
              logo,
              primaryColor,
            } = extractMenuProperties(service)
            const ArrowIcon = iconsMap[ArrowsIndexRight[index]?.name]
            // GTM event handler for right services
            const handleClick = (e: React.MouseEvent) => {
              sendBlockEvent({
                blockId: id || "2",
                componentName: "שרות",
                title: parseTitleToHour(),
                productName: data?.formDefaultInsuranceType,
                actionName: undefined,
                buttonName: link?.name,
              })
            }
            return (
              <StyledService
                key={`right-${index}`}
                index={index}
                isrightside={"true"}
                isvisible={index <= visibleIndex ? "true" : "false"}
                onClick={handleClick}
              >
                <StripWithIconAndArrow
                  link={link}
                  ArrowIcon={ArrowIcon}
                  logo={logo}
                  bgcolor={primaryColor}
                />
              </StyledService>
            )
          })}
        </StyledServicesContainer>
      )}

      {!isDesktop && (
        <>
          <CommonTitle
            title={parseTitleToHour()}
            subtitle={transformedData?.subtitle?.value}
          />
          <StyledServicesContainer>
            {services &&
              services.map((service: any, index: number) => {
                const {
                  url: link,
                  logo,
                  primaryColor,
                } = extractMenuProperties(service)
                const ArrowIcon =
                  index < leftServices.length
                    ? iconsMap[ArrowsIndexLeft[index]?.name]
                    : iconsMap[
                        ArrowsIndexRight[index - leftServices.length]?.name
                      ]

                const isRightSide = index >= leftServices.length
                return (
                  <StyledService
                    key={`Services-StyledService-${index}`}
                    index={index}
                    isrightside={isRightSide ? "true" : "false"}
                    isvisible={index <= visibleIndex ? "true" : "false"}
                  >
                    <StyledMobileStripWithIconAndArrow
                      link={link}
                      ArrowIcon={ArrowIcon}
                      logo={logo}
                      bgcolor={primaryColor}
                    />
                  </StyledService>
                )
              })}
          </StyledServicesContainer>
        </>
      )}
    </StyledBlockWrapper>
  )
}

export default Services
